// 客ビュー: 来店した客の 3D 表現を生成し、入口から現れてカフェ・席を巡って帰す
// presentation 層（v0.7.0 で動線を導入）。
//
// sim（純データ）は「いつ・どの客が来たか」と経済結果（チップ・着席・近接積分）を
// 既に確定している。ここはそれを受けて見た目の動線を演出するだけの描画専用層。
// island（自転ノード）の子なので庭と一緒に回る。
//
// 客のライフサイクル（v0.7.0）:
//   入口ゲートに出現（宇宙からふわっと降りる）
//     → カフェへ歩く（カフェ前で小ポーズ＋「☕」吹き出し＝購入）
//     → 着席セルへ歩く → 滞在（飲む。dwellSec）
//     → 入口へ戻って退店（宇宙へふわっと昇って消える）
//
// 石畳パス（connectedPathCells）があれば、客はパス上を優先して歩く（経由点に使う）。

import * as THREE from 'three';
import type { GardenGrid } from '../sim/garden';
import { GRID_SIZE, cellIndex, ENTRANCE_INDEX, cellLevel } from '../sim/garden';
import { isSeatDeco, leveledAttributes, ATTRIBUTE_KEYS, getDeco } from '../sim/decos';
import { getCustomer } from '../sim/customers';
import { gridToWorld, GARDEN_Y, CAFE_DOOR_LOCAL } from './diorama';
import { buildCustomerMesh } from './customer_mesh';

/** 各フェーズの長さ・速度（秒）。 */
const ARRIVE_SEC = 1.6; // ゲートの外から歩いて入場する（フェードイン）最大時間
const WALK_SPEED = 1.4; // ワールド単位/秒（歩行）
const BUY_PAUSE_SEC = 1.1; // カフェ前で購入ポーズ
const DEPART_SEC = 1.6; // ゲートから歩いて退場する（フェードアウト）最大時間

/**
 * 客が出現・退場する「ゲートの外側」の接近点までの距離（ワールド単位、v0.7.9）。
 * 入口セルから島の外向き（原点→入口方向）へこの距離の地点で湧き、そこへ歩いて去る。
 * 空から降下・上昇する旧演出はやめ、ゲートを地上で出入りする。
 */
const APPROACH_DIST = 1.8;

/** 行列の 1 体ぶんの間隔（ワールド単位、入口から手前へ並ぶ）。 */
const QUEUE_SPACING = 0.55;

/**
 * 行列に見せる最大人数（v0.7.9）。これを超える来店は見た目を作らない（チップ等の経済は
 * sim 側で既に確定済みなので、演出だけ間引く）。ゲートの外に大量に溜まるのを防ぐ。
 */
const MAX_QUEUE_VISIBLE = 4;

/**
 * 客の表示スケール（v0.7.2）。俯瞰ディオラマだと客が小さすぎて状態（飲んでる・並んでる・
 * 歩いてる）が読み取れないので、一回り大きく見せる。ぷるぷる・行列でもこの倍率を掛ける。
 */
const CUSTOMER_SCALE = 1.5;

/** 移動トレイル（歩いている客の後ろに引く短い光の点列）の点数。 */
const TRAIL_DOTS = 5;

/** 客の歩行フェーズ。 */
type Phase = 'arriving' | 'toCafe' | 'buying' | 'toSeat' | 'seated' | 'toExit' | 'departing';

interface Visitor {
  id: string;
  group: THREE.Group;
  bornT: number;
  dwellSec: number;
  phase: Phase;
  /** フェーズ開始の累計秒。 */
  phaseT: number;
  /** 現在の XZ（島ローカル）。 */
  pos: THREE.Vector2;
  /** 経由ウェイポイントの列（toCafe/toSeat/toExit で消費）。 */
  waypoints: THREE.Vector2[];
  /** 着席セル index（席に座る客のみ。立ち飲みは null）。 */
  seatIndex: number | null;
  /** 個体差 phase（ぷるぷる等）。 */
  wobblePhase: number;
  /** 「☕」吹き出しスプライト（buying 中だけ表示）。 */
  bubble: THREE.Sprite | null;
  /** 着席中の「飲んでる」演出（カップ吹き出し＋湯気）グループ（seated 中だけ）。 */
  drinkFx: THREE.Group | null;
  /** 移動トレイル（歩行中に後ろへ引く短い光点列）。root 直下に置く小球。 */
  trail: THREE.Mesh[];
  /** トレイル点の更新位相（古い点ほど薄く小さく）。 */
  trailIdx: number;
}

/** 入店待ちの客（入口前に並ぶ）。 */
interface QueuedCustomer {
  id: string;
  group: THREE.Group;
  pos: THREE.Vector2;
  phase: number;
}

export interface CustomerView {
  root: THREE.Group;
  /** 庭の状態を受け取り、席・お気に入りデコ・石畳パスの参照に使う。 */
  setGarden(garden: GardenGrid): void;
  /**
   * 客を 1 体スポーンする（来店演出）。tSec は現在の累計秒。
   * 同時滞在が上限に達していれば入口前の行列に並ばせ、枠が空き次第入店させる。
   */
  spawn(customerId: string, tSec: number): void;
  /** 在店客・行列をすべて取り除く（拡張島へ切替時など、客のいない島用、v0.7.1）。 */
  clear(): void;
  /** 同時滞在の上限を更新する（評判で増える）。 */
  setMaxConcurrent(n: number): void;
  readonly count: number;
  readonly queued: number;
  update(tSec: number): void;
  dispose(): void;
}

function disposeGroup(g: THREE.Object3D): void {
  g.traverse((o) => {
    const m = o as THREE.Mesh;
    if (m.geometry) m.geometry.dispose();
    const mat = m.material as THREE.Material | THREE.Material[] | undefined;
    if (Array.isArray(mat)) mat.forEach((x) => x.dispose());
    else mat?.dispose();
  });
}

function setGroupOpacity(g: THREE.Group, opacity: number): void {
  g.traverse((o) => {
    const m = (o as THREE.Mesh).material as
      | (THREE.Material & { opacity?: number; transparent?: boolean })
      | undefined;
    if (!m) return;
    m.transparent = true;
    m.opacity = opacity;
  });
}

/** 「☕」絵文字のスプライト（購入ポーズの吹き出し）。キャンバスから 1 枚作る。 */
function makeCoffeeBubble(): THREE.Sprite {
  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 64;
  const ctx = canvas.getContext('2d')!;
  // 丸い吹き出しの下地
  ctx.fillStyle = 'rgba(245,232,216,0.95)';
  ctx.beginPath();
  ctx.arc(32, 28, 24, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = '#FFB870';
  ctx.lineWidth = 3;
  ctx.stroke();
  ctx.font = '30px serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('☕', 32, 30);
  const tex = new THREE.CanvasTexture(canvas);
  tex.minFilter = THREE.NearestFilter;
  tex.magFilter = THREE.NearestFilter;
  const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false });
  const sprite = new THREE.Sprite(mat);
  sprite.scale.set(0.5, 0.5, 0.5);
  return sprite;
}

/**
 * 着席中の「飲んでいる」演出（v0.7.2）。小さなテーブルと湯気の立つカップを客の手前に置く。
 * 「ここで飲んでいる」が俯瞰でも分かるように、カップ＋立ち上る湯気パフ 3 つを持つ Group を返す。
 * update が湯気を上昇＆フェードさせる（戻り Group の userData.steam に湯気配列を入れる）。
 */
function makeDrinkFx(): THREE.Group {
  const g = new THREE.Group();
  // 小さなテーブル天板（席で飲んでいる卓）。
  const table = new THREE.Mesh(
    new THREE.CylinderGeometry(0.14, 0.14, 0.03, 12),
    new THREE.MeshStandardMaterial({ color: new THREE.Color('#D8B89A'), roughness: 0.9, flatShading: true }),
  );
  table.position.set(0, 0.34, 0);
  g.add(table);
  // カップ（クリーム＋暖色の中身）。
  const cup = new THREE.Mesh(
    new THREE.CylinderGeometry(0.045, 0.035, 0.06, 8),
    new THREE.MeshStandardMaterial({ color: new THREE.Color('#F5E8D8'), roughness: 0.8, flatShading: true }),
  );
  cup.position.set(0, 0.39, 0);
  g.add(cup);
  // 立ち上る湯気パフ（白い小球 3 つ。update でループ上昇）。
  const steam: THREE.Mesh[] = [];
  for (let i = 0; i < 3; i++) {
    const puff = new THREE.Mesh(
      new THREE.SphereGeometry(0.03, 6, 5),
      new THREE.MeshBasicMaterial({ color: new THREE.Color('#F3ECF7'), transparent: true, opacity: 0.7 }),
    );
    puff.position.set(0, 0.45, 0);
    g.add(puff);
    steam.push(puff);
  }
  g.userData.steam = steam;
  return g;
}

export function createCustomerView(): CustomerView {
  const root = new THREE.Group();
  const visitors: Visitor[] = [];
  const queue: QueuedCustomer[] = [];
  let garden: GardenGrid | null = null;
  let decoCenters: THREE.Vector2[] = [];
  /** 石畳の連結セル（入口側成分）の中心 XZ（歩行の経由点に使う）。 */
  let pathCenters: THREE.Vector2[] = [];
  /** 席セル index → そのセルの近接アンビエンススカラー（席選択の優先度）。 */
  let seatScores: { index: number; score: number; pos: THREE.Vector2 }[] = [];
  let maxConcurrent = 3;

  // 入口・カフェ前のワールド XZ（島ローカル）。
  const egx = ENTRANCE_INDEX % GRID_SIZE;
  const egy = Math.floor(ENTRANCE_INDEX / GRID_SIZE);
  const entranceW = gridToWorld(egx, egy);
  const entrancePos = new THREE.Vector2(entranceW.x, entranceW.z);
  const cafeDoor = new THREE.Vector2(CAFE_DOOR_LOCAL.x, CAFE_DOOR_LOCAL.z);
  // ゲートの外側（島の外＝原点から見て入口の先）への向きと接近点（v0.7.9）。
  // 入口は島の縁にあるので、原点→入口方向がそのまま外向き。客はここで湧いて歩いて入り、
  // 退場時はここへ歩いて去る（空からの降下/上昇はやめる）。行列もこの線上に並ぶ。
  const outward = entrancePos.lengthSq() > 1e-6
    ? entrancePos.clone().normalize()
    : new THREE.Vector2(0.7071, 0.7071);
  const approachPos = entrancePos.clone().add(outward.clone().multiplyScalar(APPROACH_DIST));

  function setMaxConcurrent(n: number): void {
    maxConcurrent = Math.max(1, Math.floor(n));
  }

  /** セルの近接アンビエンススカラー（席選択の優先度）。garden_view と同じ簡易指標。 */
  function cellContribution(idx: number): number {
    if (!garden) return 0;
    const cell = garden[idx];
    if (!cell) return 0;
    const deco = getDeco(cell.decoId);
    if (!deco) return 0;
    const leveled = leveledAttributes(deco.attributes, cellLevel(cell));
    let sum = 0;
    for (const key of ATTRIBUTE_KEYS) {
      const v = leveled[key];
      if (v != null) sum += v;
    }
    return sum;
  }

  /** 席まわり（半径2・チェビシェフ距離重み）の積分スコア。seat.ts と同じ式の軽量版。 */
  function ambianceOf(seatIdx: number): number {
    const sgx = seatIdx % GRID_SIZE;
    const sgy = Math.floor(seatIdx / GRID_SIZE);
    let total = 0;
    for (let dy = -2; dy <= 2; dy++) {
      for (let dx = -2; dx <= 2; dx++) {
        if (dx === 0 && dy === 0) continue;
        const gx = sgx + dx;
        const gy = sgy + dy;
        if (gx < 0 || gx >= GRID_SIZE || gy < 0 || gy >= GRID_SIZE) continue;
        const d = Math.max(Math.abs(dx), Math.abs(dy));
        const w = d <= 1 ? 1 - d * 0.35 : 0.3;
        total += w * cellContribution(cellIndex(gx, gy));
      }
    }
    return total;
  }

  function setGarden(g: GardenGrid): void {
    garden = g;
    const centers: THREE.Vector2[] = [];
    const seats: { index: number; score: number; pos: THREE.Vector2 }[] = [];
    for (let gy = 0; gy < GRID_SIZE; gy++) {
      for (let gx = 0; gx < GRID_SIZE; gx++) {
        const idx = cellIndex(gx, gy);
        const cell = g[idx];
        if (!cell) continue;
        const { x, z } = gridToWorld(gx, gy);
        centers.push(new THREE.Vector2(x, z));
        if (isSeatDeco(cell.decoId)) {
          seats.push({ index: idx, score: ambianceOf(idx), pos: new THREE.Vector2(x, z) });
        }
      }
    }
    decoCenters = centers;
    // 席は近接アンビエンス降順・同点は index 昇順（sim のタイブレークに合わせる）。
    seats.sort((a, b) => (b.score - a.score) || (a.index - b.index));
    seatScores = seats;

    // 石畳の連結成分の中心（歩行経由点）。動的 import を避けるためここで簡易 BFS。
    pathCenters = computePathCenters(g);
  }

  /** 入口側につながる石畳セルの中心 XZ を返す（歩行の経由点に使う）。 */
  function computePathCenters(g: GardenGrid): THREE.Vector2[] {
    const isPath = (idx: number): boolean => g[idx]?.decoId === 'stone_tile';
    const nbr = (idx: number): number[] => {
      const gx = idx % GRID_SIZE;
      const gy = Math.floor(idx / GRID_SIZE);
      const out: number[] = [];
      if (gx > 0) out.push(idx - 1);
      if (gx + 1 < GRID_SIZE) out.push(idx + 1);
      if (gy > 0) out.push(idx - GRID_SIZE);
      if (gy + 1 < GRID_SIZE) out.push(idx + GRID_SIZE);
      return out;
    };
    const visited = new Set<number>();
    const q: number[] = [];
    for (const n of nbr(ENTRANCE_INDEX)) if (isPath(n)) { visited.add(n); q.push(n); }
    while (q.length) {
      const idx = q.shift()!;
      for (const n of nbr(idx)) if (!visited.has(n) && isPath(n)) { visited.add(n); q.push(n); }
    }
    return [...visited].map((idx) => {
      const w = gridToWorld(idx % GRID_SIZE, Math.floor(idx / GRID_SIZE));
      return new THREE.Vector2(w.x, w.z);
    });
  }

  /** どの席が live 客に使われているか。 */
  function occupiedSeats(): Set<number> {
    const occ = new Set<number>();
    for (const v of visitors) if (v.seatIndex != null) occ.add(v.seatIndex);
    return occ;
  }

  /** 空席のうち近接アンビエンス最大を選ぶ（sim と同じ優先度。なければ null）。 */
  function pickSeat(): number | null {
    const occ = occupiedSeats();
    for (const s of seatScores) if (!occ.has(s.index)) return s.index;
    return null;
  }

  /** start→goal の経路を、近い石畳セルを経由点に挟んで作る（パスがあれば沿う）。 */
  function routeThroughPath(start: THREE.Vector2, goal: THREE.Vector2): THREE.Vector2[] {
    if (pathCenters.length === 0) return [goal.clone()];
    // start に最も近いパスセル → goal に最も近いパスセル の 2 点を経由（軽い「沿い」）。
    const nearest = (p: THREE.Vector2): THREE.Vector2 => {
      let best = pathCenters[0];
      let bd = Infinity;
      for (const c of pathCenters) {
        const d = c.distanceToSquared(p);
        if (d < bd) { bd = d; best = c; }
      }
      return best.clone();
    };
    const a = nearest(start);
    const b = nearest(goal);
    const wp: THREE.Vector2[] = [];
    if (a.distanceToSquared(start) > 0.04) wp.push(a);
    if (b.distanceToSquared(a) > 0.04) wp.push(b);
    wp.push(goal.clone());
    return wp;
  }

  function makePhase(seedCount: number, tSec: number): number {
    const raw = (seedCount * 0.618 + tSec * 0.37) % 1;
    return raw < 0 ? raw + 1 : raw;
  }

  /** 入店させる（入口前から歩き出す Visitor を作る）。 */
  function admit(customerId: string, tSec: number): void {
    const def = getCustomer(customerId);
    if (!def) return;
    const group = buildCustomerMesh(def.look);
    root.add(group);

    const seatIndex = pickSeat();
    // 移動トレイル（歩いている客の後ろに引く短い光点列）。root 直下に置く（客と一緒に回らない
    // ように…ではなく、root は island の子なので庭と一緒に回る＝相対位置で正しく見える）。
    const trail: THREE.Mesh[] = [];
    for (let i = 0; i < TRAIL_DOTS; i++) {
      const dot = new THREE.Mesh(
        new THREE.SphereGeometry(0.05, 6, 5),
        new THREE.MeshBasicMaterial({
          color: new THREE.Color('#FFE3A8'),
          transparent: true,
          opacity: 0,
          depthWrite: false,
        }),
      );
      dot.visible = false;
      root.add(dot);
      trail.push(dot);
    }
    const v: Visitor = {
      id: customerId,
      group,
      bornT: tSec,
      dwellSec: def.dwellSec,
      phase: 'arriving',
      phaseT: tSec,
      // ゲートの外（接近点）で湧き、入口セルまで地上を歩いて入場する（v0.7.9）。
      pos: approachPos.clone(),
      waypoints: [entrancePos.clone()],
      seatIndex,
      wobblePhase: makePhase(visitors.length, tSec),
      bubble: null,
      drinkFx: null,
      trail,
      trailIdx: 0,
    };
    visitors.push(v);
  }

  function spawn(customerId: string, tSec: number): void {
    const def = getCustomer(customerId);
    if (!def) return;
    if (visitors.length < maxConcurrent) {
      admit(customerId, tSec);
    } else if (queue.length < MAX_QUEUE_VISIBLE) {
      // 行列は上限まで。これを超える来店は演出を作らない（経済は sim 側で確定済み）。
      const group = buildCustomerMesh(def.look);
      root.add(group);
      const phase = makePhase(queue.length, tSec);
      // 行列はゲートの外（接近点付近）に並ぶので、そこを初期位置にする。
      queue.push({ id: customerId, group, pos: approachPos.clone(), phase });
    }
  }

  /** 入口前の行列を整列させ、枠が空いていれば先頭から入店させる。 */
  function serveQueue(tSec: number): void {
    while (queue.length > 0 && visitors.length < maxConcurrent) {
      const q = queue.shift()!;
      root.remove(q.group);
      disposeGroup(q.group);
      admit(q.id, tSec);
    }
    // 残った行列は入口から手前（+X+Z＝カメラ側）へ一列に整列。
    const dirX = 0.7071;
    const dirZ = 0.7071;
    for (let i = 0; i < queue.length; i++) {
      const q = queue[i];
      const tx = entrancePos.x + dirX * QUEUE_SPACING * (i + 1);
      const tz = entrancePos.y + dirZ * QUEUE_SPACING * (i + 1);
      q.pos.x += (tx - q.pos.x) * 0.12;
      q.pos.y += (tz - q.pos.y) * 0.12;
      const bob = Math.sin(tSec * 3 + q.phase * 6.28) * 0.03;
      const wob = Math.sin(tSec * 5.5 + q.phase * 6.28) * 0.06;
      q.group.scale.set(
        CUSTOMER_SCALE * (1 + wob),
        CUSTOMER_SCALE * (1 - wob),
        CUSTOMER_SCALE * (1 + wob),
      );
      q.group.position.set(q.pos.x, GARDEN_Y + bob, q.pos.y);
      q.group.rotation.y = Math.atan2(entrancePos.x - q.pos.x, entrancePos.y - q.pos.y);
    }
  }

  /** 席（または立ち飲み位置）のワールド XZ を返す。 */
  function seatPos(v: Visitor): THREE.Vector2 {
    if (v.seatIndex != null) {
      const w = gridToWorld(v.seatIndex % GRID_SIZE, Math.floor(v.seatIndex / GRID_SIZE));
      // 席のすぐ手前に座る（席メッシュに重ならないよう少しカメラ側へ）。
      return new THREE.Vector2(w.x + 0.18, w.z + 0.18);
    }
    // 立ち飲み: カフェ前の少し手前。
    return new THREE.Vector2(cafeDoor.x + 0.4, cafeDoor.y + 0.4);
  }

  function enterPhase(v: Visitor, phase: Phase, tSec: number): void {
    v.phase = phase;
    v.phaseT = tSec;
    if (phase === 'toCafe') {
      v.waypoints = routeThroughPath(v.pos, cafeDoor);
    } else if (phase === 'toSeat') {
      v.waypoints = routeThroughPath(v.pos, seatPos(v));
    } else if (phase === 'toExit') {
      v.waypoints = routeThroughPath(v.pos, entrancePos);
    } else if (phase === 'departing') {
      // 入口からゲートの外（接近点）へ地上を歩いて去る（v0.7.9。空へは昇らない）。
      v.waypoints = [approachPos.clone()];
    } else if (phase === 'buying') {
      // ☕ 吹き出しを出す。
      const bubble = makeCoffeeBubble();
      bubble.position.set(0, 0.85, 0);
      v.group.add(bubble);
      v.bubble = bubble;
    } else if (phase === 'seated') {
      // 「ここで飲んでいる」演出: カップ＋湯気を客の手前に置く（v0.7.2）。
      const fx = makeDrinkFx();
      v.group.add(fx);
      v.drinkFx = fx;
    }
    if (phase !== 'buying' && v.bubble) {
      v.group.remove(v.bubble);
      (v.bubble.material as THREE.SpriteMaterial).map?.dispose();
      (v.bubble.material as THREE.Material).dispose();
      v.bubble = null;
    }
    if (phase !== 'seated' && v.drinkFx) {
      v.group.remove(v.drinkFx);
      disposeGroup(v.drinkFx);
      v.drinkFx = null;
    }
  }

  /** ウェイポイント列に沿って pos を dt ぶん進める。到達しきったら true。 */
  function advanceAlong(v: Visitor, dt: number): boolean {
    let budget = WALK_SPEED * dt;
    while (budget > 0 && v.waypoints.length > 0) {
      const target = v.waypoints[0];
      const dx = target.x - v.pos.x;
      const dz = target.y - v.pos.y;
      const dist = Math.hypot(dx, dz);
      if (dist <= budget) {
        v.pos.copy(target);
        v.waypoints.shift();
        budget -= dist;
      } else {
        v.pos.x += (dx / dist) * budget;
        v.pos.y += (dz / dist) * budget;
        v.group.rotation.y = Math.atan2(dx, dz);
        budget = 0;
      }
    }
    return v.waypoints.length === 0;
  }

  let lastUpdateT = 0;

  function update(tSec: number): void {
    const dt = Math.min(0.1, Math.max(0, tSec - lastUpdateT));
    lastUpdateT = tSec;

    for (let i = visitors.length - 1; i >= 0; i--) {
      const v = visitors[i];
      const wob = Math.sin(tSec * 5.5 + v.wobblePhase * 6.28) * 0.06;
      const bob = Math.sin(tSec * 3 + v.wobblePhase * 6.28) * 0.04;
      let y = GARDEN_Y + bob;
      let opacity = 1;

      switch (v.phase) {
        case 'arriving': {
          // ゲートの外から入口まで地上を歩いて入場（フェードイン）。空からは降りない。
          const t = (tSec - v.phaseT) / ARRIVE_SEC;
          opacity = Math.min(1, t * 1.6);
          const reached = advanceAlong(v, dt);
          if (reached || t >= 1) {
            v.pos.copy(entrancePos);
            enterPhase(v, 'toCafe', tSec);
          }
          break;
        }
        case 'toCafe': {
          if (advanceAlong(v, dt)) enterPhase(v, 'buying', tSec);
          break;
        }
        case 'buying': {
          if (tSec - v.phaseT >= BUY_PAUSE_SEC) enterPhase(v, 'toSeat', tSec);
          // 吹き出しはふわっと上下。
          if (v.bubble) v.bubble.position.y = 0.85 + Math.sin(tSec * 4) * 0.05;
          break;
        }
        case 'toSeat': {
          if (advanceAlong(v, dt)) enterPhase(v, 'seated', tSec);
          // 着席先を向く間も向き更新は advanceAlong 内。
          break;
        }
        case 'seated': {
          if (tSec - v.phaseT >= v.dwellSec) enterPhase(v, 'toExit', tSec);
          break;
        }
        case 'toExit': {
          if (advanceAlong(v, dt)) enterPhase(v, 'departing', tSec);
          break;
        }
        case 'departing': {
          // 入口からゲートの外へ地上を歩いて退場（フェードアウト）。空へは昇らない。
          const t = (tSec - v.phaseT) / DEPART_SEC;
          const reached = advanceAlong(v, dt);
          opacity = Math.max(0, 1 - t);
          if (reached || t >= 1) {
            root.remove(v.group);
            if (v.bubble) v.group.remove(v.bubble);
            for (const d of v.trail) { root.remove(d); disposeGroup(d); }
            disposeGroup(v.group);
            visitors.splice(i, 1);
            continue;
          }
          break;
        }
      }

      // ぷるぷる待機（スカッシュ&ストレッチ）。客を一回り大きく見せる（v0.7.2）。
      v.group.scale.set(
        CUSTOMER_SCALE * (1 + wob),
        CUSTOMER_SCALE * (1 - wob),
        CUSTOMER_SCALE * (1 + wob),
      );
      setGroupOpacity(v.group, opacity);
      v.group.position.set(v.pos.x, y, v.pos.y);

      // 移動トレイル: 歩行中（toCafe/toSeat/toExit）だけ後ろへ短い光点を引く（v0.7.2）。
      const walking = v.phase === 'toCafe' || v.phase === 'toSeat' || v.phase === 'toExit';
      updateTrail(v, walking, tSec, opacity);

      // 「飲んでいる」湯気を上昇＆ループフェード（着席中だけ）。
      if (v.drinkFx) {
        const steam = v.drinkFx.userData.steam as THREE.Mesh[] | undefined;
        if (steam) {
          for (let s = 0; s < steam.length; s++) {
            const ph = (tSec / 1.6 + s / steam.length) % 1;
            const puff = steam[s];
            puff.position.set(Math.sin(ph * 6.28 + s) * 0.02, 0.45 + ph * 0.22, 0);
            (puff.material as THREE.MeshBasicMaterial).opacity = Math.sin(ph * Math.PI) * 0.7;
          }
        }
      }
    }

    serveQueue(tSec);
  }

  /**
   * 移動トレイル（歩いている客の後ろに引く短い光点列）を更新する（v0.7.2）。
   * walking 中は一定間隔で現在地に新しい点を「置き」、古い点ほど薄く小さくフェードさせる。
   * 歩行をやめたら全点をフェードアウト（visible=false へ）。
   */
  function updateTrail(v: Visitor, walking: boolean, tSec: number, baseOpacity: number): void {
    if (walking) {
      // 一定間隔（~0.12s）で次の点を現在地スナップ。リング状に巡回。
      const stamp = Math.floor(tSec / 0.12);
      if (stamp !== v.trailIdx) {
        v.trailIdx = stamp;
        const cursor = stamp % v.trail.length;
        const dot = v.trail[cursor];
        dot.visible = true;
        dot.position.set(v.pos.x, GARDEN_Y + 0.05, v.pos.y);
        dot.userData.bornStamp = stamp;
      }
    }
    // 各点を経過に応じてフェード（古いほど薄く小さく。歩行停止後は自然に消える）。
    for (const dot of v.trail) {
      if (!dot.visible) continue;
      const age = v.trailIdx - (dot.userData.bornStamp ?? v.trailIdx);
      const life = 1 - age / v.trail.length;
      if (life <= 0 || !walking) {
        const m = dot.material as THREE.MeshBasicMaterial;
        m.opacity = Math.max(0, m.opacity - 0.08);
        if (m.opacity <= 0.01) dot.visible = false;
      } else {
        const m = dot.material as THREE.MeshBasicMaterial;
        m.opacity = life * 0.55 * baseOpacity;
        dot.scale.setScalar(0.5 + life * 0.7);
      }
    }
  }

  function dispose(): void {
    for (const v of visitors) {
      disposeGroup(v.group);
      for (const d of v.trail) disposeGroup(d);
    }
    visitors.length = 0;
    for (const q of queue) disposeGroup(q.group);
    queue.length = 0;
  }

  /** 在店客・行列をすべて即時に取り除く（v0.7.1: 拡張島へ切替時など、客のいない島用）。 */
  function clear(): void {
    for (const v of visitors) {
      root.remove(v.group);
      disposeGroup(v.group);
      for (const d of v.trail) { root.remove(d); disposeGroup(d); }
    }
    visitors.length = 0;
    for (const q of queue) {
      root.remove(q.group);
      disposeGroup(q.group);
    }
    queue.length = 0;
  }

  void decoCenters; // お気に入りデコ参照は将来用に保持（現状は席優先の動線）。

  return {
    root,
    setGarden,
    spawn,
    clear,
    setMaxConcurrent,
    get count() {
      return visitors.length;
    },
    get queued() {
      return queue.length;
    },
    update,
    dispose,
  };
}
